// Guided setup, step 1: Employee details. Name and work email are read-only (from the authorized directory entry, else "Not available"); the hire date was confirmed
// when HR setup started and is not edited here.
// Saving uses the existing employment-change and personal-detail routes (employeeDetails.tsx) - effective on the hire date for the first job terms.
//
// Cycle 12 save correctness: the form is a snapshot taken when the step opened. A field the person never touched FOLLOWS the server's current value (it can never be
// "found different" and written back as a stale blank), and ONLY touched fields are sent (null only for a deliberate clear). See detailsModel.ts.
import { useEffect, useRef, useState } from 'react';
import { OWN_RECORD_MESSAGE, EmployeeDetailsFields, detailsChanged, detailsPhoneProblem, saveEmployeeDetails, useDirectoryOptions, valuesFromBaseline, type DetailsValues } from './employeeDetails';
import { adoptBaseline, editedFields, pruneTouched, touch, valuesForSave, type Touched } from './detailsModel';
import type { StepContext } from './setupTypes';

export function DetailsStep({ ctx, isOwnRecord }: { ctx: StepContext; isOwnRecord: boolean }) {
  const baseline = ctx.view.summary.details;
  const options = useDirectoryOptions();
  const [values, setValues] = useState<DetailsValues>(() => valuesFromBaseline(baseline));
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const touchedRef = useRef<Touched>({});
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  /** Every edit marks exactly the fields it changed as touched. */
  function onChange(next: DetailsValues) {
    touchedRef.current = touch(touchedRef.current, ...editedFields(valuesRef.current, next));
    valuesRef.current = next;
    setValues(next);
  }

  // The server's view moved (a reload after a partial save, or another change): fields already saved stop counting as edits, and untouched fields follow the server.
  useEffect(() => {
    touchedRef.current = pruneTouched(touchedRef.current, valuesRef.current, baseline);
    const adopted = adoptBaseline(valuesRef.current, touchedRef.current, baseline);
    if (JSON.stringify(adopted) !== JSON.stringify(valuesRef.current)) { valuesRef.current = adopted; setValues(adopted); }
  }, [baseline]);

  // The shell asks this step to save before it moves on. Changed job fields (employment-change route) and the personal phone (personal-detail route) go through
  // the existing guarded routes; then details.saved is recorded. The baseline is the server's reading of the term in effect on the hire date.
  useEffect(() => {
    ctx.bindSaver(async () => {
      const b = baselineRef.current;
      if (isOwnRecord) return { ok: false, message: OWN_RECORD_MESSAGE };
      if (!b.hireDate) return { ok: false, message: 'The hire date is missing for this record, so the details cannot be confirmed. Nothing was saved.' };
      const r = await saveEmployeeDetails({ employeeId: ctx.employeeId, effectiveDate: b.hireDate, baseline: b, values: valuesForSave(b, valuesRef.current, touchedRef.current) });
      if (!r.ok) {
        // Partial success (job details saved, personal phone not): re-read what is really on record so a retry compares against it, never a stale snapshot.
        if (r.saved) await ctx.reload();
        return r;
      }
      if (r.wrote) await ctx.reload();
      return { ok: true, decisions: { details: { saved: true } } };
    });
    return () => ctx.bindSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.employeeId, isOwnRecord]);

  const effective = valuesForSave(baseline, values, touchedRef.current);
  const phoneIssue = detailsPhoneProblem(baseline, effective);
  useEffect(() => {
    const c = detailsChanged(baseline, effective);
    ctx.setDirty(c.job.length > 0 || c.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, baseline]);

  // An invalid personal phone blocks Save & continue (the shell shows this message) without discarding anything the person typed.
  const invalidMessage = phoneIssue ? `Personal phone: ${phoneIssue}` : null;
  useEffect(() => {
    ctxRef.current.bindFooter({ invalidMessage });
  }, [invalidMessage]);
  useEffect(() => () => ctxRef.current.bindFooter(null), []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 sm:p-6" data-testid="step-details">
      <h2 className="text-base font-semibold text-gray-900">Employee details</h2>
      <p className="text-xs text-gray-500 mt-0.5 mb-4">Job details are optional to fill in now; anything left unassigned can be added later from the employee’s HR page.</p>
      {isOwnRecord && <p className="mb-4 rounded-md bg-[#EDF5FC] px-3 py-2 text-xs text-gray-700" data-testid="own-record-setup-note">{OWN_RECORD_MESSAGE}</p>}
      <EmployeeDetailsFields
        idPrefix="setup-details"
        employeeId={ctx.employeeId}
        person={ctx.person}
        baseline={baseline}
        values={values}
        onChange={onChange}
        options={options}
        disabled={isOwnRecord}
        hireDateNote="Confirmed when HR setup started."
      />
      {options.error && (
        <p role="alert" className="mt-3 text-xs text-red-700" data-testid="details-options-error">{options.error} <button type="button" className="underline" onClick={options.retry}>Try again</button></p>
      )}
    </div>
  );
}
